import React, { useContext, useState, useEffect } from 'react';
import clsx from 'clsx';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

import Layout from '../../common_components/Layout';
import InnerModal from '../components/InnerModal';
import { AppContext } from '../contexts';
import { getNextStep, getLastCompletedStep } from '../utils';
import { ThrashBin, CheckMark, PlusSignCircled } from '../../icons';

export default function Projects({ name }) {
  const app = useContext(AppContext);
  const api = app.APIs.projects;
  const maximumNumberOfProjects = 4;

  const [projects, setProjects] = useState([]);
  const [stepState, setStepState] = useState('initial');
  const [projectToDelete, setProjectToDelete] = useState();

  useEffect(() => {
    api.getProjectsList().then((data) => {
      setProjects(data.sort((a, b) => new Date(a.added) - new Date(b.added)));
    });
  }, []); // TODO: move to template view and get data as context? (ate does it this way now)

  async function handleProjectSelect(projectId) {
    const project = await api.getProject(projectId);
    app.setActiveProject(project);
    const newStepCandidate = getLastCompletedStep(project.steps);
    app.setVisibleStep(getNextStep(newStepCandidate ?? 'projects'));
  }
  async function handleProjectDeleteBtn(projectId) {
    if (projectId !== app.activeProject?.id) {
      setProjectToDelete(projects.find((p) => p.id === projectId));
      setStepState('delete-confirm');
    } else {
      setStepState('delete-unable');
    }
  }
  async function handleAddNewProjectBtn() {
    if (projects.length < maximumNumberOfProjects) {
      setStepState('adding-project');
    } else {
      setStepState('project-add-unable');
    }
  }

  return (
    <Layout
      sideContent={
        <>
          <h2 className="text-white">Dostępne projekty</h2>
          <ul className="list-none bg-slate-500 basis-2/3 rounded-md py-4 px-2 min-h-32">
            {projects.map((project) =>
              project.description?.replace(/\s/g, '') ? (
                <Tippy
                  content={project.description}
                  placement="top"
                  theme="custom-dark"
                >
                  <li
                    key={project.id}
                    onClick={() => handleProjectSelect(project.id)}
                    className={clsx(
                      'flex items-center cursor-pointer hover:bg-white/50 hover:text-success rounded-lg px-4 py-1 group before:font-normal break-all',
                      app.activeProject?.id == project.id && 'font-bold',
                    )}
                  >
                    {project.name}
                    {project.id == app.activeProject?.id && (
                      <span className="ml-2">
                        <CheckMark />
                      </span>
                    )}
                    <span
                      className="ml-auto text-transparent hover:scale-110  transition-transform group-hover:text-error"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleProjectDeleteBtn(project.id);
                      }}
                    >
                      <ThrashBin />
                    </span>
                  </li>
                </Tippy>
              ) : (
                <li
                  key={project.id}
                  onClick={() => handleProjectSelect(project.id)}
                  className={clsx(
                    'flex items-center cursor-pointer hover:bg-white/50 hover:text-success rounded-lg px-4 py-1 group before:font-normal break-all',
                    app.activeProject?.id == project.id && 'font-bold',
                  )}
                >
                  {project.name}
                  {project.id == app.activeProject?.id && (
                    <span className="ml-2">
                      <CheckMark />
                    </span>
                  )}
                  <span
                    className="ml-auto text-transparent hover:scale-110  transition-transform group-hover:text-error"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleProjectDeleteBtn(project.id);
                    }}
                  >
                    <ThrashBin />
                  </span>
                </li>
              ),
            )}
          </ul>
          <button
            className="btn btn-success flex items-center gap-2 text-white"
            onClick={handleAddNewProjectBtn}
          >
            <PlusSignCircled />
            Nowy Projekt
          </button>
          <ul className="list-none [&_a]:text-info pl-0 self-end text-end">
            <li>
              <a href={app.downloadFiles?.dataExample} download>
                przykładowe dane
              </a>
            </li>
            <li>
              <a
                href="/static/norm_air_guide.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                opis przykładowych danych
              </a>
            </li>
          </ul>
        </>
      }
      title="Wybierz projekt"
      mainContent={
        <>
          <h2 className="text-info">
            Wybierz jeden z projektów z listy w panelu bocznym aby przejśc dalej
          </h2>
          {stepState == 'adding-project' && (
            <InnerModal>
              <NewProjectForm
                setStepState={setStepState}
                projectApi={api}
                setProjects={setProjects}
                thisStepName={name}
              />
            </InnerModal>
          )}
          {stepState == 'delete-confirm' && (
            <InnerModal>
              <ConfirmProjectDeleteAlert
                setStepState={setStepState}
                projectName={projectToDelete.name}
                projectId={projectToDelete.id}
                setProjects={setProjects}
              ></ConfirmProjectDeleteAlert>
            </InnerModal>
          )}
          {stepState == 'delete-unable' && (
            <InnerModal>
              <UnableToDeleteProjectAlert setStepState={setStepState} />
            </InnerModal>
          )}
          {stepState == 'project-add-unable' && (
            <InnerModal>
              <UnableToAddProjectAlert setStepState={setStepState} />
            </InnerModal>
          )}
        </>
      }
    />
  );
}

function NewProjectForm({
  setStepState,
  projectApi,
  setProjects,
  thisStepName,
}) {
  const handleFormSubmit = (event) => {
    event.preventDefault();

    const name = event.target.elements.name.value;
    const description = event.target.elements.description.value;
    // since we are using data to determine which steps were completed
    // we need to populated "data" appropriately when creating a project.
    const data = { [thisStepName]: { completed: true } };
    projectApi
      .addProject({
        name,
        description,
        data,
      })
      .then((data) => {
        setProjects((p) => [...p, { name, description, data, id: data.id }]);
      });
    setStepState('loading');
  };
  const handleCancelBtn = () => {
    setStepState('initial');
  };

  return (
    <>
      <h2 className="mt-0">Nowy projekt</h2>
      <form className="mt-0 min-w-80" onSubmit={handleFormSubmit}>
        <label className="form-control w-full max-w-xs">
          <div className="label">
            <span className="label-text">Nazwa projektu *</span>
          </div>
          <input
            type="text"
            name="name"
            className="input input-bordered w-full max-w-xs text-black"
            maxLength={100}
            required
          />
        </label>
        <label className="form-control">
          <div className="label">
            <span className="label-text">Opis projektu</span>
          </div>
          <textarea
            className="textarea input-bordered h-24 text-black"
            name="description"
            minLength={10}
            maxLength={300}
          ></textarea>
        </label>
        <div className="flex justify-between w-full">
          <button className="btn mt-4" onClick={handleCancelBtn}>
            Anuluj
          </button>
          <button type="submit" className="btn btn-success mt-4">
            Zapisz
          </button>
        </div>
      </form>
    </>
  );
}

function UnableToDeleteProjectAlert({ setStepState }) {
  return (
    <div className="flex flex-col w-full h-full">
      <h2 className="mt-0 mb-8">Nie można usunąć aktywnego projektu</h2>
      <p className="mb-12">
        Zmień projekt lub odświez stronę aby wykonać operację.
      </p>
      <button className="btn" onClick={() => setStepState('initial')}>
        Powrót
      </button>
    </div>
  );
}

function UnableToAddProjectAlert({ setStepState }) {
  return (
    <div className="flex flex-col w-full h-full">
      <h2 className="mt-0 mb-12 text-warning">
        Osiągnięto maksymalną liczbę projektów
      </h2>
      <button className="btn" onClick={() => setStepState('initial')}>
        Powrót
      </button>
    </div>
  );
}

function ConfirmProjectDeleteAlert({
  setStepState,
  projectName,
  projectId,
  setProjects,
}) {
  const app = useContext(AppContext);
  const api = app.APIs.projects;

  const handleConfirmBtn = () => {
    const projectWasDeleted = api.deleteProject(projectId);
    // TODO: handle delete failed
    if (projectWasDeleted) {
      setProjects((p) => p.filter((project) => project.id !== projectId));
      setStepState('initial');
    }
  };
  const handleCancelBtn = () => {
    setStepState('initial');
  };

  return (
    <>
      <h2 className="mt-0 break-all max-w-xl">
        Czy na pewno chcesz usunąć projekt "{projectName}"?
      </h2>
      <div className="flex justify-between w-full">
        <button className="btn mt-4" onClick={handleCancelBtn}>
          Anuluj
        </button>
        <button
          type="submit"
          className="btn btn-error mt-4"
          onClick={handleConfirmBtn}
        >
          Potwierdź
        </button>
      </div>
    </>
  );
}
